import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Outlet, useNavigate, Link } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline'
import { Sidebar, SidebarSection } from './Sidebar'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/contexts/ThemeContext'
import { Avatar } from '@/components/common/Avatar'
import { NotificationDropdown } from '@/components/features/notifications/NotificationDropdown'
import { useAdminPaymentVerifications } from '@/hooks/usePaymentProofs'

// Base sections visible to any admin role.
const baseAdminSections: SidebarSection[] = [
    {
        title: 'Dashboard',
        items: [
            { to: '/admin', icon: 'dashboard', label: 'Overview' },
        ],
    },
    {
        title: 'Manage',
        items: [
            { to: '/admin/courses', icon: 'school', label: 'Courses', requiredPermission: 'course.create' },
            { to: '/admin/users', icon: 'group', label: 'Kelola Peserta', requiredPermission: 'user.list' },
            { to: '/admin/instructors', icon: 'cast_for_education', label: 'Kelola Guru', requiredPermission: 'user.list' },
            { to: '/admin/batches', icon: 'calendar_month', label: 'Batches', requiredPermission: 'batch.create' },
            { to: '/admin/content-review', icon: 'rate_review', label: 'Content Review', requiredPermission: 'course.edit' },
            { to: '/admin/ktp-review', icon: 'badge', label: 'Verifikasi KTP', requiredPermission: 'ktp.view' },
            { to: '/admin/memberships', icon: 'emoji_events', label: 'Membership', requiredPermission: 'user.list' },
            { to: '/admin/certificates', icon: 'workspace_premium', label: 'Sertifikat', requiredPermission: 'course.edit' },
            { to: '/admin/settings', icon: 'settings', label: 'Pengaturan', requiredPermission: 'settings.edit_system' },
            { to: '/admin/notifications/compose', icon: 'notifications_active', label: 'Kirim Notifikasi', requiredPermission: 'user.list' },
        ],
    },
    {
        title: 'Finance',
        items: [
            { to: '/admin/payment-verifications', icon: 'verified', label: 'Verifikasi Pembayaran', requiredPermission: 'finance.process_payment' },
            { to: '/admin/withdrawals', icon: 'payments', label: 'Withdrawals', requiredPermission: 'finance.approve_withdrawal' },
            { to: '/admin/commissions', icon: 'attach_money', label: 'Commissions', requiredPermission: 'finance.edit_commission' },
            { to: '/admin/coupons', icon: 'local_offer', label: 'Kupon', requiredPermission: 'course.edit' },
        ],
    },
    {
        title: 'Assessment',
        items: [
        { to: '/admin/quizzes', icon: 'quiz', label: 'Quiz Reviews', requiredPermission: 'course.edit' },
        { to: '/admin/assignments', icon: 'assignment', label: 'Assignment Reviews', requiredPermission: 'course.edit' },
        { to: '/admin/grading', icon: 'grading', label: 'Grading', requiredPermission: 'course.edit' },
        ],
    },
    {
        title: 'Oversight',
        items: [
            { to: '/admin/audit-logs', icon: 'history', label: 'Audit Logs', requiredPermission: 'audit.view' },
        ],
    },
]

// Super-admin-only surfaces. Hidden from vanilla admins; backend remains
// the real authority (API returns 403 PERMISSION_DENIED on attempt).
const superAdminSection: SidebarSection = {
    title: 'Super Admin',
    items: [
        { to: '/admin/staff', icon: 'supervisor_account', label: 'Kelola Staff', requiredPermission: 'user.create' },
        { to: '/admin/privileges', icon: 'admin_panel_settings', label: 'Permission Admin', requiredPermission: 'user.manage_permissions' },
        { to: '/admin/system-settings', icon: 'tune', label: 'System Settings', requiredPermission: 'settings.edit_system' },
        { to: '/admin/smtp-settings', icon: 'mail', label: 'SMTP Settings', requiredPermission: 'settings.edit_system' },
        { to: '/admin/bank-config', icon: 'account_balance', label: 'Bank Pembayaran', requiredPermission: 'settings.edit_system' },
    ],
}

export const AdminLayout: React.FC = () => {
    const { user, logout, isSuperAdmin } = useAuth()
    const { data: paymentData } = useAdminPaymentVerifications('', 1, 1)
    const pendingPaymentCount = paymentData?.pendingCount || 0

    const allSections: SidebarSection[] = isSuperAdmin()
        ? [...baseAdminSections, superAdminSection]
        : baseAdminSections

    const sections = useMemo(() => {
        if (!user) return allSections

        return allSections
            .map((section) => ({
                ...section,
                items: section.items.filter((item) => {
                    if (!item.requiredPermission) return true
                    if (user.role === 'super_admin') return true
                    return user.permissions?.includes(item.requiredPermission) ?? false
                }),
            }))
            .filter((section) => section.items.length > 0)
            .map((section) => ({
                ...section,
                items: section.items.map((item) => {
                    if (item.to === '/admin/payment-verifications') {
                        return { ...item, badge: pendingPaymentCount }
                    }
                    return item
                }),
            }))
    }, [allSections, user, pendingPaymentCount])
    const navigate = useNavigate()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const { mode, toggleMode } = useTheme()

    // Lock body scroll when mobile sidebar is open
    useEffect(() => {
        if (sidebarOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => { document.body.style.overflow = '' }
    }, [sidebarOpen])

    const handleLogout = async () => {
        await logout()
        navigate('/login')
    }

    return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-main dark:text-white font-body">
            <Sidebar
                sections={sections}
                title="Pelatihan Merdeka"
                accentColor="primary"
                logo="/logo.svg"
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />

            {/* Main Content */}
            <main className="md:ml-64 min-h-screen">
                {/* Top Bar */}
                <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 md:px-8 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-md shadow-sm dark:shadow-none dark:border-b dark:border-white/5">
                    <div className="flex items-center gap-3 md:gap-4">
                        {/* Mobile hamburger menu */}
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="md:hidden size-10 rounded-lg bg-slate-100 dark:bg-surface-card flex items-center justify-center hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[20px]">menu</span>
                        </button>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={toggleMode}
                            className="size-10 rounded-lg bg-slate-100 dark:bg-surface-card flex items-center justify-center hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                            aria-label={`switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
                        >
                            {mode === 'light' ? (
                                <MoonIcon className="h-5 w-5" />
                            ) : (
                                <SunIcon className="h-5 w-5" />
                            )}
                        </button>
                        <NotificationDropdown userId={user?.id} role={user?.role || 'admin'} />

                        {/* Profile Dropdown */}
                        <Menu as="div" className="relative">
                            <Menu.Button className="flex items-center gap-3 pl-4 border-l border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 rounded-lg py-2 pr-3 transition-colors cursor-pointer">
                                <Avatar
                                    name={user?.name || 'Guest'}
                                    size="sm"
                                    className="ring-2 ring-primary/30"
                                />
                                <div className="hidden md:block text-left">
                                    <p className="text-sm font-bold text-slate-900 dark:text-white">{user?.name || 'Admin'}</p>
                                    <p className="text-xs text-slate-500 dark:text-text-muted">Superadmin</p>
                                </div>
                                <span className="material-symbols-outlined text-[18px] text-slate-400">expand_more</span>
                            </Menu.Button>

                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg bg-white dark:bg-surface-card shadow-lg ring-1 ring-gray-100 dark:ring-white/10 focus:outline-none overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-100 dark:border-white/10">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{user?.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-text-muted truncate">{user?.email}</p>
                                    </div>
                                    <div className="py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/admin/profile"
                                                    className={`${active ? 'bg-gray-100 dark:bg-white/5' : ''} flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300`}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">person</span>
                                                    Profile
                                                </Link>
                                            )}
                                        </Menu.Item>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/"
                                                    className={`${active ? 'bg-gray-100 dark:bg-white/5' : ''} flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300`}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">home</span>
                                                    Back to Home
                                                </Link>
                                            )}
                                        </Menu.Item>
                                    </div>
                                    <div className="border-t border-gray-100 dark:border-white/10 py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    onClick={handleLogout}
                                                    className={`${active ? 'bg-red-50 dark:bg-red-500/10' : ''} flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 dark:text-red-400 w-full text-left`}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">logout</span>
                                                    Logout
                                                </button>
                                            )}
                                        </Menu.Item>
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>
                </header>

                {/* Page Content */}
                <div className="p-4 md:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    )
}

export default AdminLayout
